import React from "react";
import { Link } from "react-router-dom";
import { DISCLAIMERS } from "@/lib/compliance";

/**
 * Reusable consent block: GLBA-aligned privacy disclosure + E-SIGN /
 * UETA electronic-communications checkbox. Shared by InquiryForm and
 * ContactDialog so the wording matches the consent_text payload sent to
 * /api/inquiries.
 *
 * Props:
 *   - checked, onChange  → controlled <input type=checkbox>
 *   - error              → error message for the checkbox
 *   - compact            → ContactDialog uses a smaller layout
 */
export default function InquiryConsent({
    checked,
    onChange,
    error,
    compact = false,
}) {
    return (
        <>
            <div
                className={
                    compact
                        ? "mt-4 space-y-2 rounded-md border border-[rgba(212,175,55,0.18)] bg-[rgba(212,175,55,0.03)] p-3 text-[11px] leading-relaxed text-brand-muted"
                        : "mt-6 space-y-3 rounded-md border border-[rgba(212,175,55,0.18)] bg-[rgba(212,175,55,0.03)] p-4 text-xs leading-relaxed text-brand-muted"
                }
            >
                <p data-testid="form-privacy-notice">
                    We collect the information you submit to respond to your
                    insurance inquiry, evaluate coverage options, match you
                    with a licensed producer, prepare submissions when
                    appropriate, maintain communication and consent records,
                    and comply with applicable law. Please review our{" "}
                    <Link
                        to="/privacy"
                        data-testid="form-privacy-link"
                        className="text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                    >
                        Privacy Notice
                    </Link>{" "}
                    and{" "}
                    <Link
                        to="/terms"
                        data-testid="form-terms-link"
                        className="text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                    >
                        Terms of Service
                    </Link>{" "}
                    before submitting.
                </p>
                {!compact && (
                    <p data-testid="form-sensitive-warning">
                        <span className="text-brand-goldSoft">
                            Do not submit:{" "}
                        </span>
                        {DISCLAIMERS.sensitiveInfoWarning.replace(
                            /^Do not include /,
                            ""
                        )}
                    </p>
                )}
                <p data-testid="form-contact-consent">
                    {DISCLAIMERS.contactConsent}
                </p>
            </div>

            <div className={compact ? "mt-3" : "mt-4"}>
                <label
                    htmlFor="e_consent"
                    className={`flex items-start gap-3 rounded-md border bg-[#0b0e13] ${
                        compact ? "p-3 text-[11px]" : "p-4 text-xs"
                    } leading-relaxed transition-colors ${
                        error
                            ? "border-red-500/60"
                            : "border-[rgba(212,175,55,0.25)]"
                    }`}
                >
                    <input
                        id="e_consent"
                        data-testid="form-e-consent-checkbox"
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                        aria-required="true"
                        aria-invalid={!!error}
                        aria-describedby={
                            error ? "e_consent-error" : undefined
                        }
                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#d4af37] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                    />
                    <span className="text-brand-muted">
                        <span className="text-brand-ivory">
                            Electronic communications consent.
                        </span>{" "}
                        {DISCLAIMERS.electronicConsent}
                    </span>
                </label>
                {error && (
                    <p
                        id="e_consent-error"
                        role="alert"
                        className="mt-1 text-xs text-red-400"
                    >
                        {error}
                    </p>
                )}
            </div>
        </>
    );
}
